import elevarTracking from './elevar-tracking';
import trapFocus from './trap-focus';

function giftCardModal(opts) {
  const config = {
    dom: {
      banner: '.js-quickview-banner-image',
      button: '.js-atc-btn',
      custom_amount: 'customAmount',
      date: 'digitalDate',
      gift_note: 'giftCardNote',
      gift_note_close: '.js-gift-note-close',
      gift_note_overlay: 'giftCardNoteOverlay',
      option: 'giftModalDeliveryOption',
      price: 'giftModalPrice',
      title: 'giftModalTitle',
      forms: {
        physical: {
          form: 'physicalForm',
          email: 'physicalSenderEmail',
        },
        digital: {
          form: 'digitalForm',
          email: 'digitalRecipientEmail',
          phone: 'digitalRecipientNumber',
        },
        input: '.js-giftnote-input',
      },
    },
    cls: {
      hidden: 'hide',
      closing: 'is-quickview-closing',
      expose: 'is-quickview-exposed',
      error: 'error',
    },
  };

  const c = __.extend(config, opts);

  const $body = document.body;
  const $note = document.getElementById(c.dom.gift_note);
  const $atcButton = document.querySelectorAll(c.dom.button);
  const $inputs = document.querySelectorAll(c.dom.forms.input);
  const $giftNoteCloseEls = document.querySelectorAll(c.dom.gift_note_close);
  const $giftNoteOverlay = document.getElementById(c.dom.gift_note_overlay);
  const $option = document.getElementById(c.dom.option);
  const $price = document.getElementById(c.dom.price);
  const $title = document.getElementById(c.dom.title);
  const $physicalForm = document.getElementById(c.dom.forms.physical.form);
  const $physicalEmail = document.getElementById(c.dom.forms.physical.email);
  const $digitalForm = document.getElementById(c.dom.forms.digital.form);
  const $digitalEmail = document.getElementById(c.dom.forms.digital.email);
  const $digitalNumber = document.getElementById(c.dom.forms.digital.phone);
  const $dateValue = document.getElementById(c.dom.date);
  const $customAmount = document.getElementById(c.dom.custom_amount);

  let price = null;
  let deliveryOption = null;
  let activeVariant = null;
  let atcCallback = null;

  // setup state
  const formOptions = {
    senderEmail: null,
    senderName: null,
    recipientName: null,
    recipientEmail: null,
    recipientNumber: null,
    sendDate: $dateValue?.value || null,
  };
  const formHandler = {
    set(target, key, value) {
      target[key] = value;

      validateForm();
      return true;
    },
  };
  const formValues = new Proxy(formOptions, formHandler);

  function init(giftCard, variant, cb = false) {
    const { amount, digitalOption } = giftCard || {};
    activeVariant = variant || null;
    price = amount;
    deliveryOption = digitalOption || 'Physical';

    if (cb) {
      atcCallback = cb;
    }

    if (!$title || !$price || !$option) {
      return;
    }

    renderContent();
    addEvents();
  }

  /**
   * Add Events
   * @description Adds open and closing events to the modal
   * @returns {void}
   */
  function addEvents() {
    if ($giftNoteCloseEls?.length) {
      [...$giftNoteCloseEls].forEach(($giftNoteClose) => {
        __.addEvent({
          id: $giftNoteClose,
          event: 'click',
          fn: closeGiftNote,
        });
      });
    }

    if ($giftNoteOverlay) {
      __.addEvent({
        id: $giftNoteOverlay,
        event: 'click',
        fn: closeGiftNote,
      });
    }

    [...$atcButton].forEach((el) => {
      __.addEvent({
        id: el,
        event: 'click',
        fn: (e) => {
          if (activeVariant && activeVariant !== 'null') {
            const { id, giftTemplate } = activeVariant || null;
            addToCart(e.target, id, giftTemplate);
          }
        },
      });
    });

    [...$inputs].forEach((el) => {
      __.addEvent({
        id: el,
        event: 'blur',
        fn: (e) => {
          if (e?.target) {
            if (isEmptyOrSpaces(e.target.value)) {
              __.addClass(e.target, c.cls.error);
              collectFormValues(e.target);
            } else {
              __.removeClass(e.target, c.cls.error);
            }
          }
        },
      });
    });
  }

  /**
   * Add Form Events
   * @description Adds form events to the modal
   * @returns {void}
   */
  function addFormEvents() {
    __.addEvent({
      id: $physicalForm,
      event: 'change',
      fn: (e) => {
        if (e?.target?.value) {
          collectFormValues(e.target);
        }
      },
    });

    __.addEvent({
      id: $digitalForm,
      event: 'change',
      fn: (e) => {
        if (e?.target?.value) {
          collectFormValues(e.target);
        }
      },
    });
  }

  /**
   * Render Content
   * @description Renders the content of the modal
   * @returns {void}
   */
  function renderContent() {
    if (price && deliveryOption) {
      setHeading();
      setForms();
    }
  }

  /**
   * Set Heading
   * @description Sets the heading with the current price and delivery option
   * @returns {void}
   */
  function setHeading() {
    $price.innerHTML = `${price}`;
    $option.innerHTML = `${deliveryOption}`;
  }

  /**
   * Set Forms
   * @description Sets the correct form based on delivery option
   * @returns {void}
   */
  function setForms() {
    if (!$digitalForm && !$physicalForm) return;

    switch (deliveryOption) {
      case 'send via sms':
        __.addClass($physicalForm, c.cls.hidden);
        __.addClass($digitalEmail, c.cls.hidden);
        __.removeClass($digitalNumber, c.cls.hidden);
        __.removeClass($digitalForm, c.cls.hidden);

        addFormEvents();
        return;
      case 'send via email':
        __.addClass($digitalNumber, c.cls.hidden);
        __.addClass($physicalForm, c.cls.hidden);
        __.removeClass($digitalForm, c.cls.hidden);
        __.removeClass($digitalEmail, c.cls.hidden);

        addFormEvents();
        return;
      case 'print at home':
        __.addClass($digitalForm, c.cls.hidden);
        __.removeClass($physicalEmail, c.cls.hidden);
        __.removeClass($physicalForm, c.cls.hidden);

        addFormEvents();
        return;
      default:
        __.addClass($digitalForm, c.cls.hidden);
        __.addClass($physicalEmail, c.cls.hidden);
        __.removeClass($physicalForm, c.cls.hidden);

        addFormEvents();
    }
  }

  /**
   * Open Gift Note
   * @description Opens the gift note drawer
   * @returns {void}
   */
  function openGiftNote() {
    __.addClass($body, c.cls.expose);

    if ($note) {
      // wait for drawer to open before trapping focus
      setTimeout(() => {
        trapFocus($note);
      }, 300);
    }
  }

  /**
   * Close Gift Note
   * @description Closes the gift note drawer
   * @returns {void}
   */
  function closeGiftNote() {
    // close quickview drawer
    __.addClass($body, c.cls.closing);
    __.removeClass($body, c.cls.expose);

    // full hide quickview drawer after close animation
    setTimeout(() => {
      [...$atcButton].forEach((el) => {
        el.disabled = true;
      });
      [...$inputs].forEach((el) => {
        el.value = '';
      });
      __.removeClass($body, c.cls.closing);
      resetFormValues();
    }, 500);
  }

  /**
   * Collect Form Values
   * @description Collects the form values and stores them in the formValues object
   * @param {object} el - The form element
   * @returns {void}
   */
  function collectFormValues(el) {
    const { id, value } = el || null;

    function updateValue(val) {
      if (isEmptyOrSpaces(val)) {
        return null;
      }
      return val;
    }

    if (!id) return;
    const inputName = id.toLowerCase().replace(/(digital|physical)/g, '');
    let val;

    switch (inputName) {
      case 'sendername':
        val = updateValue(value);

        formValues.senderName = val;
        break;
      case 'senderemail':
        val = updateValue(value);

        formValues.senderEmail = val;
        break;
      case 'recipientname':
        val = updateValue(value);

        formValues.recipientName = val;
        break;
      case 'recipientemail':
        val = updateValue(value);

        formValues.recipientEmail = val;
        break;
      case 'recipientnumber':
        val = updateValue(value);

        formValues.recipientNumber = val;
        break;
      case 'date':
        val = updateValue(value);

        formValues.sendDate = val;
        break;
      default:
        val = updateValue(value);

        formValues.recipientGiftNote = val;
    }
  }

  /**
   * Validate Form
   * @description Validates the form and enables the add to cart button
   * @returns {void}
   */
  function validateForm() {
    const delivery = deliveryOption.toLowerCase();
    const invalidInputs = [...$inputs].filter((el) => !el.checkValidity());

    if (invalidInputs.length > 0) {
      $atcButton[0].disabled = true;
      $atcButton[1].disabled = true;
      return;
    }

    function anyNull(...args) {
      return args.some((arg) => arg === null);
    }

    if (delivery === 'physical') {
      const {
        senderName,
        recipientName,
        recipientGiftNote,
      } = formValues || null;

      if (anyNull(senderName, recipientName, recipientGiftNote)) {
        $atcButton[0].disabled = true;
      } else {
        $atcButton[0].disabled = false;
      }
    }

    if (delivery === 'print at home') {
      const {
        senderName,
        senderEmail,
        recipientName,
        recipientGiftNote,
      } = formValues || null;

      if (anyNull(senderName, senderEmail, recipientName, recipientGiftNote)) {
        $atcButton[0].disabled = true;
      } else {
        $atcButton[0].disabled = false;
      }
    }

    if (delivery === 'send via email') {
      const {
        sendDate,
        recipientName,
        recipientEmail,
        recipientGiftNote,
      } = formValues || null;

      if (anyNull(sendDate, recipientName, recipientEmail, recipientGiftNote)) {
        $atcButton[1].disabled = true;
      } else {
        $atcButton[1].disabled = false;
      }
    }

    if (delivery === 'send via sms') {
      const {
        sendDate,
        recipientName,
        recipientNumber,
        recipientGiftNote,
      } = formValues || null;

      if (anyNull(sendDate, recipientName, recipientNumber, recipientGiftNote)) {
        $atcButton[1].disabled = true;
      } else {
        $atcButton[1].disabled = false;
      }
    }
  }

  /**
   * Is Empty Or Spaces
   * @description Checks if a string is empty or only contains spaces
   * @param {string} str - The string to check
   * @returns {boolean} true if empty or only contains spaces, false if not
   */
  function isEmptyOrSpaces(str) {
    if (!str || !str.trim()) {
      return true;
    }

    return false;
  }

  /**
   * Reset Form Values
   * @description Resets the form values
   * @returns {void}
   */
  function resetFormValues() {
    formValues.senderEmail = null;
    formValues.senderName = null;
    formValues.recipientName = null;
    formValues.recipientEmail = null;
    formValues.recipientNumber = null;
    formValues.sendDate = null;
    formValues.recipientGiftNote = null;
  }

  /**
   * Add To Cart
   * @description Adds the gift card to the cart
   * @param {string} id - The product id
   * @param {string} giftTemplate - The gift template
   * @returns {void}
   */
  function addToCart(el, id, giftTemplate) {
    const baseURL = SDG.Data.apiUrl;
    const { cid } = SDG.Data;
    // remove any null values in order to submit without error
    const formProperties = Object.keys(formValues).reduce((acc, key) => {
      if (formValues[key] !== null) {
        acc[key] = formValues[key];
      }

      return acc;
    }, {});

    const min = Number($customAmount.min);
    const max = Number($customAmount.max);

    if ($customAmount.value >= min && $customAmount.value <= max) {
      formProperties.customAmount = $customAmount.value;
    }

    const properties = {
      digitalGiftCardTemplate: giftTemplate,
      ...formProperties,
    };

    const postData = {
      items: [
        {
          id: `${id}`,
          quantity: 1,
          properties,
        },
      ],
    };

    el.disabled = true;

    __.ajax({
      url: `${baseURL}/cart/add?cid=${cid}`,
      type: 'POST',
      data: JSON.stringify(postData),
      contentType: 'application/json',
      success(itemData) {
        if (typeof atcCallback === 'function') {
          atcCallback();
        }
        closeGiftNote();
        SDG.Data.setDisplayItemAdded(id);

        // elevar tracking
        const addedItem = itemData.items.find((item) => (
          item.variant_id === `${id}`
        ));
        elevarTracking(addedItem, 'add');
      },
      error(err) {
        el.disabled = false;
        console.error(err);
      },
    });
  }

  return {
    init,
    openGiftNote,
    closeGiftNote,
  };
}

export default giftCardModal;
